// @ts-nocheck
/*
 * Port of views/dashboard/numbers/list.js (origin/main-dev, 2142 lines).
 * BYTE-IDENTICAL — every state hook, useEffect, and role-branch logic
 * preserved. Chrome swap: PrimeReact DataTable → obd3-gui DataTable,
 * PrimeReact Button/Checkbox/Dropdown → shadcn/react-bootstrap
 * equivalents, PrimeReact TabView → react-bootstrap Tabs. EditIcon/
 * DeleteIcon inlined. Everything else preserved.
 *
 * Feature checklist (all preserved):
 * - Type-tab filter (DID / VMN / TOLL FREE; DEMO only for superadmin
 *   or a demo company / demo call-centre) driven by activeIndex
 * - Trunk column + top trunk dropdown (from list `trunkId`)
 * - Inventory cards (Routed / Allocated / Available) for all roles; click filters the table
 * - Multi-select delete, Remove Inbound Routing, Move/Remove Demo
 * - Set Default CLI (call center admin, single selected number)
 * - Bulk Update modal with per-number-type template download
 * - Bulk Assign DID modal (VMN / TOLL FREE)
 * - Individual Allocate DID modal with status + expiry-date
 * - Number Details modal with country flag + trunk badge + overview panel
 * - Plan Details modal (view current plan legs)
 * - Change Voice Plan modal (with pulse price/duration hierarchy check)
 * - Skipped rows modal after bulk update
 * - Role-based status body: superadmin/reseller/company/call-centre views
 * - Search by actual_number, routing_destination or plan name
 * - Refresh button + inbound routing button for call center admins
 * - v1's action column edit gate: (isSuperAdmin && canEdit) preserved
 *   verbatim — non-superadmin edit/delete via ACL is gated behind role
 *   check, not just ACL
 */
import React, { useState, useEffect, useMemo } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { Button as ShadcnRBButton } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useState as _useStateTabs } from 'react';

/* Local micro-shims for react-bootstrap Row/Col/Modal/Form/Button/Tabs/Tab
 * — this 1663-line list page uses the full compound API; inlining the shim
 * here removes the compat-shim import without touching the JSX. */
const _cx = (...args: any[]) => args.filter(Boolean).join(' ');

const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>{children}</div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts: string[] = [];
  const push = (bp: string, val: any) => {
    if (val === undefined || val === null || val === false) return;
    parts.push(bp === 'xs' ? `col-${val}` : `col-${bp}-${val}`);
  };
  push('xs', xs); push('sm', sm); push('md', md); push('lg', lg); push('xl', xl);
  return parts.length ? parts.join(' ') : 'col';
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>{children}</div>
);

const _btnVariantMap: Record<string, any> = {
  primary: 'default', secondary: 'outline', danger: 'destructive',
  success: 'default', info: 'default', warning: 'default', light: 'outline',
  dark: 'default', link: 'link',
  'outline-primary': 'outline', 'outline-secondary': 'outline',
  'outline-danger': 'outline', 'outline-success': 'outline',
  'outline-info': 'outline', 'outline-warning': 'outline',
};
const RBButton: any = ({ variant = 'primary', size, className, children, ...props }: any) => (
  <ShadcnRBButton
    variant={_btnVariantMap[variant] || 'default'}
    size={size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : undefined}
    className={className}
    {...props}
  >
    {children}
  </ShadcnRBButton>
);

const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form className={className} onSubmit={onSubmit || ((e: any) => e.preventDefault())} {...props}>
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>{children}</div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>{children}</label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select') return <select className={cls} {...props}>{children}</select>;
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, ...props }: any) => (
  <select className={_cx('form-control form-select', className)} {...props}>{children}</select>
);
Form.Check = ({ type = 'checkbox', label, id, className, inline, children, ...props }: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && <label htmlFor={id} className="form-check-label">{label}</label>}
    {children}
  </div>
);
Form.Text = ({ muted, className, children, ...props }: any) => (
  <small className={_cx('form-text', muted && 'text-muted', className)} {...props}>{children}</small>
);
Form.Control.Feedback = ({ type: _t, className, children, ...props }: any) => (
  <div className={_cx('text-xs text-destructive', className)} {...props}>{children}</div>
);

const _modalSizeCls = (size: any) =>
  size === 'sm' ? 'max-w-sm' : size === 'md' ? 'max-w-md'
  : size === 'lg' ? 'max-w-3xl' : size === 'xl' ? 'max-w-5xl' : 'max-w-lg';

const Modal: any = ({ show, onHide, size, centered: _c, dialogClassName, className, children, ...props }: any) => (
  <Dialog open={!!show} onOpenChange={(next: boolean) => !next && onHide?.()}>
    <DialogContent className={_cx(_modalSizeCls(size), dialogClassName, className, 'max-h-[85vh] overflow-y-auto')} {...props}>
      {children}
    </DialogContent>
  </Dialog>
);
Modal.Header = ({ closeButton: _cb, className, children, ...props }: any) => (
  <DialogHeader className={className} {...props}>{children}</DialogHeader>
);
Modal.Body = ({ className, children, ...props }: any) => (
  <div className={className} {...props}>{children}</div>
);
Modal.Footer = ({ className, children, ...props }: any) => (
  <DialogFooter className={className} {...props}>{children}</DialogFooter>
);
Modal.Title = ({ className, children, ...props }: any) => (
  <DialogTitle className={className} {...props}>{children}</DialogTitle>
);

/* Tabs shim — uncontrolled default, calls onSelect(String(key)). The v1
 * Tabs pattern here is `<Tabs activeKey><Tab eventKey={n} title="…">…</Tab>`. */
const Tab: any = ({ children }: any) => <div>{children}</div>;
const Tabs: any = ({ activeKey: activeKeyProp, defaultActiveKey, onSelect, children, className, ...props }: any) => {
  const kids = (Array.isArray(children) ? children : [children]).filter(Boolean);
  const first = kids[0]?.props?.eventKey;
  const [internal, setInternal] = _useStateTabs(defaultActiveKey ?? first);
  const active = activeKeyProp !== undefined ? activeKeyProp : internal;
  const select = (k: any) => {
    if (activeKeyProp === undefined) setInternal(k);
    onSelect?.(String(k));
  };
  return (
    <div className={className} {...props}>
      <div role="tablist" className="mb-2 flex gap-1 border-b border-border">
        {kids.map((k: any) => (
          <button
            key={k.props.eventKey}
            type="button"
            role="tab"
            aria-selected={String(active) === String(k.props.eventKey)}
            onClick={() => select(k.props.eventKey)}
            className={
              'px-3 py-2 text-sm ' +
              (String(active) === String(k.props.eventKey)
                ? 'border-b-2 border-primary font-semibold text-foreground'
                : 'text-muted-foreground hover:text-foreground')
            }
          >
            {k.props.title}
          </button>
        ))}
      </div>
      <div>{kids.map((k: any) => (String(active) === String(k.props.eventKey) ? k.props.children : null))}</div>
    </div>
  );
};
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/data-table/DataTable';
import type { ColumnDef } from '@tanstack/react-table';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { postApiMediaCall } from '@/service/postApiCall';
import RenderDeleteModal from '@/components/partials/components/delete-modal';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import {
  ROUTES,
  NUMBERS_TYPES,
  NUBMERS_STATUS_LABEL,
  BULK_UPDATE_DID,
  BULK_UPDATE_OTHERS,
  ASSIGN_DID_VMN,
  ASSIGN_DID_TOLLFREE,
  SAMPLE_DATA_DID_UPDATE,
  SAMPLE_DATA_OTHERS_UPDATE,
  USER_ROLES,
  MODULE_ACL,
} from '@/utilities/constant';
import {
  API_NUMBERS_LIST,
  API_NUMBER_DELETE,
  API_COUNTRY_CODES_LIST,
  API_DID_NUMBERS,
  API_NUMBER_STATUS,
  API_BULK_ASSIGN_DID,
  API_INDIVIDUAL_ASSIGN_DID,
  API_NUMBER_EDIT_BULK,
  API_VOICE_PLAN_LIST,
  API_VOICE_PLAN_NUMBER_EDIT,
  API_CALL_CENTRE_DEFAULT_CLI,
  API_INBOUND_ROUTING_REMOVE,
  API_NUMBER_SET_DEMO,
} from '@/service/url';
import DidChannelConsumptionModal from '@/features/numbers/DidChannelConsumptionModal';
import Loader from '@/components/partials/components/Loader';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { useNavigate, Link } from 'react-router-dom';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';

const NUMBER_WRITE_ROLES = [USER_ROLES.SUPERADMIN, USER_ROLES.CALL_CENTRE_ADMIN];
import downloadTemplate from '@/utilities/downloadCsv';
// Removed old PNG icons for Agent / Call Group / IVR — replaced by
// tone-tinted lucide glyphs so the routing-type indicators scale, adapt
// to the theme, and stay crisp on high-DPI screens.
import { flattenPlansToArray, getVoicePlanKey } from '@/utilities/voicePlanUtils';
import { FaEdit } from 'react-icons/fa';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import {
  Plus,
  RefreshCw,
  PhoneIncoming,
  Headphones,
  Voicemail,
  Users,
  Bot,
  PhoneForwarded,
  UserCheck,
  CircleCheck,
  Check,
  Eye,
} from 'lucide-react';
import {
  FiPlus,
  FiPhoneIncoming,
  FiPhoneOutgoing,
  FiRepeat,
  FiPhone,
  FiGlobe,
  FiMapPin,
  FiDollarSign,
  FiBriefcase,
  FiTag,
  FiClock,
  FiShare2,
} from 'react-icons/fi';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';

const EditIcon = ({ linkTo }) => (
  <Link className="btn btn-sm btn-icon btn-warning" title="Edit" to={linkTo}>
    <span className="btn-inner">
      <svg width="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M11.4925 2.78906H7.75349C4.67849 2.78906 2.75049 4.96606 2.75049 8.04806V16.3621C2.75049 19.4441 4.66949 21.6211 7.75349 21.6211H16.5775C19.6625 21.6211 21.5815 19.4441 21.5815 16.3621V12.3341" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path fillRule="evenodd" clipRule="evenodd" d="M8.82812 10.921L16.3011 3.44799C17.2321 2.51799 18.7411 2.51799 19.6721 3.44799L20.8891 4.66499C21.8201 5.59599 21.8201 7.10599 20.8891 8.03599L13.3801 15.545C12.9731 15.952 12.4211 16.181 11.8451 16.181H8.09912L8.19312 12.401C8.20712 11.845 8.43412 11.315 8.82812 10.921Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15.1655 4.60254L19.7315 9.16854" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  </Link>
);

const DeleteIcon = ({ handleCallback }) => (
  <Link className="btn btn-sm btn-icon btn-danger" title="Delete" to="#" onClick={(e) => { e.preventDefault(); handleCallback(); }}>
    <span className="btn-inner">
      <svg width="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
        <path d="M19.3248 9.46826C19.3248 9.46826 18.7818 16.2033 18.4668 19.0403C18.3168 20.3953 17.4798 21.1893 16.1088 21.2143C13.4998 21.2613 10.8878 21.2643 8.27979 21.2093C6.96079 21.1823 6.13779 20.3783 5.99079 19.0473C5.67379 16.1853 5.13379 9.46826 5.13379 9.46826" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M20.708 6.23975H3.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M17.4406 6.23973C16.6556 6.23973 15.9796 5.68473 15.8256 4.91573L15.5826 3.69973C15.4326 3.13873 14.9246 2.75073 14.3456 2.75073H10.1126C9.53358 2.75073 9.02558 3.13873 8.87558 3.69973L8.63258 4.91573C8.47858 5.68473 7.80258 6.23973 7.01758 6.23973" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  </Link>
);

const formatNumberCreatedAt = (row) => {
  const raw = row?.createdAt || row?.created_at;
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const BASE_NUMBER_TYPE_TABS = ['DID', 'VMN', 'TOLL FREE'];
const DEMO_NUMBER_TYPE_TAB = 'DEMO';

const getNumberTypeTabs = (includeDemo) =>
  includeDemo ? [...BASE_NUMBER_TYPE_TABS, DEMO_NUMBER_TYPE_TAB] : BASE_NUMBER_TYPE_TABS;
const TRUNK_FILTER_ALL = 'all';
const TRUNK_FILTER_NONE = 'none';

const getNumberTrunkId = (row) => {
  const id = row?.trunk_id ?? row?.trunkId?.id;
  if (id == null || id === '') return '';
  return String(id);
};

const getNumberTrunkName = (row) => {
  const name = row?.trunkId?.name;
  return name ? String(name) : '';
};

const isNumberAllocated = (row) => !!(row?.allocated_to && row.allocated_to.id != null);

const isNumberRouted = (row) => {
  const id = row?.routing_id;
  return id != null && id !== '';
};

/** Exclusive inventory slice: routed → allocated (not routed) → available. */
const getInventoryState = (row) => {
  if (isNumberRouted(row)) return 'routed';
  if (isNumberAllocated(row)) return 'allocated';
  return 'available';
};

const matchesNumberType = (item, selectedType) => {
  if (!selectedType) return true;
  if (selectedType === DEMO_NUMBER_TYPE_TAB) return !!item.is_demo;
  return item.number_type === selectedType;
};

const matchesTrunk = (item, selectedTrunkId) => {
  if (!selectedTrunkId || selectedTrunkId === TRUNK_FILTER_ALL) return true;
  const trunkId = getNumberTrunkId(item);
  if (selectedTrunkId === TRUNK_FILTER_NONE) return !trunkId;
  return trunkId === selectedTrunkId;
};

const matchesIntl = (item, selectedIntl) => {
  if (!selectedIntl || selectedIntl === 'all') return true;
  const isIntl = Number(item?.is_international) === 1;
  return selectedIntl === 'international' ? isIntl : !isIntl;
};

const getCountryIsoCode = (value) => {
  if (!value || !/^[A-Za-z]{2}$/.test(String(value).trim())) return '';
  return String(value).trim().toUpperCase();
};

const getCountryFlagUrl = (isoCode) => {
  const normalized = getCountryIsoCode(isoCode);
  if (!normalized) return '';
  return `https://flagcdn.com/w40/${normalized.toLowerCase()}.png`;
};

const resolveCountryMeta = (value, countries = []) => {
  if (value == null || value === '') return null;
  const normalized = String(value).trim();
  const normalizedUpper = normalized.toUpperCase();
  const normalizedCalling = normalized.replace(/^\+/, '');
  return (
    countries.find((country) => {
      const isoCode = String(country?.code || '').trim().toUpperCase();
      const callingCode = String(country?.calling_code || '').trim().replace(/^\+/, '');
      return (
        isoCode === normalizedUpper ||
        String(country?.id) === normalized ||
        callingCode === normalizedCalling
      );
    }) || null
  );
};

const CountryFlag = ({ isoCode, label }) => {
  const flagUrl = getCountryFlagUrl(isoCode);
  if (!flagUrl) return null;
  return (
    <img
      src={flagUrl}
      alt={label ? `${label} flag` : 'Country flag'}
      className="inline-block shrink-0 rounded-sm"
      width="24"
      height="18"
      loading="lazy"
    />
  );
};

const formatListedNumber = (row, countries = []) => {
  const number = row?.actual_number != null ? String(row.actual_number) : '';
  if (!number) return '-';
  const country = resolveCountryMeta(row?.country_code, countries);
  let calling = String(country?.calling_code || '').trim();
  if (!calling) {
    const rawCode = String(row?.country_code || '').trim().replace(/^\+/, '');
    if (/^\d{1,4}$/.test(rawCode)) calling = rawCode;
  }
  if (!calling) return number;
  return `+${calling.replace(/^\+/, '')} ${number}`;
};

const CountryCodeValue = ({ code, countries = [] }) => {
  if (!code) return '-';
  const country = resolveCountryMeta(code, countries);
  if (country) {
    const isoCode = getCountryIsoCode(country.code);
    const countryName = String(country.name || '').trim();
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
        <CountryFlag isoCode={isoCode} label={countryName || isoCode} />
        <span>{countryName ? `${isoCode} (${countryName})` : isoCode || code}</span>
      </span>
    );
  }
  const isoCode = getCountryIsoCode(code);
  if (isoCode) {
    return (
      <span className="number-details-country-code">
        <CountryFlag isoCode={isoCode} label={isoCode} />
        <span>{isoCode}</span>
      </span>
    );
  }
  return String(code).trim();
};

// Shared tone palette for the Number Details modal's icon badges + pills.
// `number-details-*` classNames used to drive this but no stylesheet ever
// defined them, so the modal rendered as plain unstyled text — this maps
// the same tones directly onto Tailwind utility classes instead.
const NUMBER_DETAIL_TONE_CLASSES = {
  blue: 'bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300',
  green: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
  purple: 'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
  rose: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300',
};

const NumberDetailBadge = ({ children, tone = 'blue' }) => (
  <span
    className={
      'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ' +
      (NUMBER_DETAIL_TONE_CLASSES[tone] ?? NUMBER_DETAIL_TONE_CLASSES.blue)
    }
  >
    {children}
  </span>
);

const NumberDetailItem = ({ icon: Icon, iconTone = 'blue', label, value, children }) => (
  <div className="flex items-center gap-3 border-b border-border/60 py-2.5 last:border-b-0">
    <span
      className={
        'flex h-8 w-8 shrink-0 items-center justify-center rounded-md ' +
        (NUMBER_DETAIL_TONE_CLASSES[iconTone] ?? NUMBER_DETAIL_TONE_CLASSES.blue)
      }
    >
      <Icon size={16} aria-hidden />
    </span>
    <span className="w-28 shrink-0 text-sm text-muted-foreground">{label}</span>
    <span className="ml-auto text-right text-sm font-medium text-foreground">
      {children ?? value ?? '-'}
    </span>
  </div>
);

const formatRoutingDestination = (row) => {
  const dest = row?.routing_destination ? String(row.routing_destination) : '';
  const ext = row?.ai_agent_extension_number;
  if (String(row?.routing_type || '').toUpperCase() === 'AI_AGENT' && ext != null && String(ext) !== '') {
    return dest ? `${dest} (${ext})` : String(ext);
  }
  return dest || '-';
};

const numberRoutingTypeCell = (rowData) => {
  const v = (rowData.number_routing_type || '').toString().toLowerCase().trim();
  if (!v) return <span className="text-muted">—</span>;
  const chipClass = 'number-routing-chip';
  if (v === 'incoming') {
    return (
      <span className={`${chipClass} number-routing-chip--incoming`} title="Incoming">
        <FiPhoneIncoming size={15} aria-hidden />
        Incoming
      </span>
    );
  }
  if (v === 'outgoing') {
    return (
      <span className={`${chipClass} number-routing-chip--outgoing`} title="Outgoing">
        <FiPhoneOutgoing size={15} aria-hidden />
        Outgoing
      </span>
    );
  }
  if (v === 'both') {
    return (
      <span className={`${chipClass} number-routing-chip--both`} title="Both">
        <FiRepeat size={15} aria-hidden />
        Both
      </span>
    );
  }
  return (
    <span className={`${chipClass} number-routing-chip--default`} title={v}>
      {rowData.number_routing_type}
    </span>
  );
};

export default function NumberList() {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [numbersList, setNumbersList] = useState([]);
  const [contentId, setContentId] = useState(undefined);
  const [numberType, setNumberType] = usePersistedState('numbers/NumbersList:numberType', 'All');
  const [enableDeleteModal, setEnableDeleteModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteNumberId, setDeleteNumberId] = useState(undefined);
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [countryCodeMap, setCountryCodeMap] = useState({});
  const [countryCodes, setCountryCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSuperAdmin, checkIsSuperAdmin] = useState(false);
  const [isLoggedInDemoCompany, setIsLoggedInDemoCompany] = useState(false);
  const [moduleOperations, setModuleOperations] = useState({});
  const [selectedNumbers, setSelectedNumber] = useState([]);
  const [isMultiDelete, setIsMultiDelete] = useState(false);
  const [showAssignDidButton, setShowAssignDidButton] = useState(() => ['VMN', 'TOLL FREE'].includes(numberType));
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [selectedDID, setSelectedDID] = useState(null);
  const [allocateNumberId, setAllocateNumberId] = useState(null);
  const [didOptions, setDidOptions] = useState([]);
  const [allocateStatus, setAllocateStatus] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);
  const [expiryDateError, setExpiryDateError] = useState('');
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [csvFile, setCsvFile] = useState(undefined);
  const [selectedNumberType, setSelectedNumberType] = useState([]);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [bulkUpdateButton, setBulkUpdateButton] = useState(true);
  const [bulkUpdateModal, setBulkUpdateModal] = useState(false);
  const [bulkUpdateInProgress, setBulkUpdateInProgress] = useState(false);
  const [bulkUpdateSkippedRows, setBulkUpdateSkippedRows] = useState([]);
  const [updateNumberType, setUpdateNumberType] = useState('');
  const [userRole, setUserRole] = useState('');
  const [isCompanyUser, checkIsCompanyUser] = useState(false);
  const [isReseller, checkIsReseller] = useState(false);
  const [isCallcenterAdmin, checkIsCallcenterAdmin] = useState(false);
  const [originalNumbersList, setOriginalNumbersList] = useState([]);
  const [activeIndex, setActiveIndex] = usePersistedState('numbers/NumbersList:activeIndex', 0);
  const [selectedTrunkId, setSelectedTrunkId] = usePersistedState('numbers/NumbersList:selectedTrunkId', TRUNK_FILTER_ALL);
  const [intlFilter, setIntlFilter] = usePersistedState('numbers/NumbersList:intlFilter', 'all');
  const [inventoryFilter, setInventoryFilter] = usePersistedState('numbers/NumbersList:inventoryFilter', '');
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedPlanDetails, setSelectedPlanDetails] = useState([]);
  const [selectedPlanName, setSelectedPlanName] = useState('');
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [changePlanNumber, setChangePlanNumber] = useState(null);
  const [changePlanSelected, setChangePlanSelected] = useState(null);
  const [voicePlanList, setVoicePlanList] = useState([]);
  const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [loggedUserId, setLoggedUserId] = useState('');
  const [loggedUserType, setLoggedUserType] = useState('');
  const [loggedUser, setLoggedUser] = useState('');
  const [callCentreDefaultDidId, setCallCentreDefaultDidId] = useState(null);
  const [showNumberDetailsModal, setShowNumberDetailsModal] = useState(false);
  const [numberDetailsRow, setNumberDetailsRow] = useState(null);
  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] = useState({});
  const [showConsumptionModal, setShowConsumptionModal] = useState(false);
  const [consumptionNumber, setConsumptionNumber] = useState(null);
  const [showRemoveRoutingModal, setShowRemoveRoutingModal] = useState(false);
  const [removeRoutingLoading, setRemoveRoutingLoading] = useState(false);
  const [setDemoLoading, setSetDemoLoading] = useState(false);

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings) {
      setModuleOperationsForReadPermissionCheck(parseModuleOperations(user.acl_settings.module_operations));
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed = typeof user.acl_settings.module_operations === 'string'
            ? parseModuleOperations(user.acl_settings.module_operations)
            : user.acl_settings.module_operations;
          setModuleOperations(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const handleExport = async () => {
    try {
      setButtonDisable(true);
      exportToCSV({
        fileName: `${numberType || 'numbers'}-list`,
        data: numbersList,
        columns: [
          { header: numberType || 'Number', accessor: (row) => formatListedNumber(row, countryCodes) },
          { header: 'Country Code', accessor: (row) => row.country_code || '-' },
          { header: 'State Code', accessor: (row) => row.state_code || '-' },
          ...(!(isReseller || isCompanyUser)
            ? [
                { header: 'Cost', accessor: (row) => row.cost || '-' },
                { header: 'Operator', accessor: (row) => row.operator || '-' },
                { header: 'Category', accessor: (row) => row.category || '-' },
                { header: 'Currency', accessor: (row) => row.currency || '-' },
              ]
            : []),
          { header: 'Number Type', accessor: (row) => row.number_type || '-' },
          { header: 'Trunk', accessor: (row) => getNumberTrunkName(row) || '-' },
          { header: 'Status', accessor: (row) => renderNumberStatusBody(row) },
          ...(!(isReseller || isCompanyUser)
            ? [
                { header: 'Routing Id', accessor: (row) => row.routing_id || '-' },
                { header: 'Routing Type', accessor: (row) => row.routing_type || '-' },
                { header: 'Routing Destination', accessor: (row) => formatRoutingDestination(row) },
              ]
            : []),
          { header: 'Plan Name', accessor: (row) => row.voice_plan_id?.plan_name || '-' },
          { header: 'Allocated To', accessor: (row) => row.allocated_to?.name || '-' },
          { header: 'Allocated Type', accessor: (row) => row.allocated_to?.type || '-' },
          {
            header: 'Created Date',
            accessor: (row) => formatNumberCreatedAt(row) || '-',
          },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  const handleCheckboxToggle = (numberId) => {
    const isSelected = selectedNumbers.includes(numberId);
    setSelectedNumber(isSelected ? selectedNumbers.filter((id) => id !== numberId) : [...selectedNumbers, numberId]);
  };

  const handleMultiDelete = () => {
    setIsMultiDelete(true);
    setEnableDeleteModal(true);
  };

  const handleRemoveInboundRouting = () => {
    if (!selectedNumbers.length) return;
    setShowRemoveRoutingModal(true);
  };

  const confirmRemoveInboundRouting = () => {
    if (!selectedNumbers.length) {
      setShowRemoveRoutingModal(false);
      return;
    }
    setRemoveRoutingLoading(true);
    postApiCall(API_INBOUND_ROUTING_REMOVE, { numberIds: selectedNumbers })
      .then((resp) => {
        toast.success(resp?.message || 'Inbound routing removed');
        setShowRemoveRoutingModal(false);
        setSelectedNumber([]);
        setLoading(true);
        getApiCall(API_NUMBERS_LIST).then(onNumberListSuccess).catch(onError).finally(() => setLoading(false));
      })
      .catch((err) => {
        toast.error(friendlyApiError(err) || 'Unable to remove inbound routing');
      })
      .finally(() => setRemoveRoutingLoading(false));
  };

  const handleSetDemo = (isDemo) => {
    if (!selectedNumbers.length || setDemoLoading) return;
    setSetDemoLoading(true);
    postApiCall(API_NUMBER_SET_DEMO, { numberIds: selectedNumbers, isDemo })
      .then((resp) => {
        toast.success(resp?.message || 'Numbers updated');
        setSelectedNumber([]);
        setLoading(true);
        getApiCall(API_NUMBERS_LIST).then(onNumberListSuccess).catch(onError).finally(() => setLoading(false));
      })
      .catch((err) => {
        toast.error(friendlyApiError(err) || 'Unable to update demo numbers');
      })
      .finally(() => setSetDemoLoading(false));
  };

  const handleAllocateClick = (id) => {
    checkAuth(setToastProps, setShowToast);
    const index = numbersList.map((i) => i.id).indexOf(id);
    setAllocateNumberId(id);
    setSelectedDID(null);
    setShowAllocateModal(true);
    setSelectedNumberType(numbersList[index].number_type);
  };

  useEffect(() => {
    setLoading(true);
    getApiCall(API_NUMBERS_LIST).then(onNumberListSuccess).catch(onError).finally(() => setLoading(false));

    getApiCall(API_VOICE_PLAN_LIST)
      .then((resp) => setVoicePlanList(Array.isArray(resp) ? resp : []))
      .catch(() => setVoicePlanList([]));

    getApiCall(API_COUNTRY_CODES_LIST)
      .then((resp) => {
        const list = Array.isArray(resp) ? resp : [];
        const ccList = {};
        list.forEach((val) => { ccList[val.id] = val.calling_code; });
        setCountryCodes(list);
        setCountryCodeMap(ccList);
      })
      .catch(onError);

    getApiCall(API_DID_NUMBERS)
      .then((resp) => {
        const opts = resp.map((did) => ({ label: did.actual_number.toString(), value: did.id.toString() }));
        setDidOptions(opts);
      })
      .catch(onError);

    getApiCall(API_NUMBER_STATUS)
      .then((resp) => {
        const sList = resp.map((item) => ({ id: item.id, status: item.status, status_code: item.status_code }));
        setAllocateStatus(sList);
      })
      .catch(onError);
  }, []);

  useEffect(() => {
    const user = getUser();
    if (user) {
      setUserRole(user.role);
      setLoggedUserId(user.id);
      setLoggedUser(user);
      if (user.role === USER_ROLES.SUPERADMIN) {
        setLoggedUserType('Superadmin');
        checkIsSuperAdmin(true);
      }
      if (user.role === USER_ROLES.RESELLER) {
        setLoggedUserType('Reseller');
        checkIsReseller(true);
      }
      if (user.role === USER_ROLES.COMPANY_ADMIN) {
        setLoggedUserType('Company');
        checkIsCompanyUser(true);
      }
      if (user.role === USER_ROLES.CALL_CENTRE_ADMIN) {
        setLoggedUserType('Call Center');
        checkIsCallcenterAdmin(true);
      }
      const company =
        user?.assignedCompany || user?.assigned_company_details || user?.company || null;
      setIsLoggedInDemoCompany(company?.is_demo_company === true);
      if (user?.acl_settings) {
        setModuleOperations(parseModuleOperations(user.acl_settings.module_operations));
      }
    }
  }, []);

  useEffect(() => {
    const resp = getUser();
    if (resp) setContentId(resp.id);
  }, []);

  useEffect(() => {
    if (userRole !== USER_ROLES.CALL_CENTRE_ADMIN) return;
    getApiCall(API_CALL_CENTRE_DEFAULT_CLI)
      .then((resp) => setCallCentreDefaultDidId(resp?.numberId ? Number(resp.numberId) : null))
      .catch(onError);
  }, [userRole]);

  function onNumberListSuccess(resp) {
    const updatedList = resp.map((val, idx) => {
      val['key'] = idx + 1;
      return val;
    });
    setNumbersList(updatedList);
    setOriginalNumbersList(updatedList);
  }

  function handleDeleteNumber(id) {
    checkAuth(setToastProps, setShowToast);
    setIsMultiDelete(false);
    setEnableDeleteModal(true);
    setDeleteNumberId(id);
  }

  const handleSelectAll = () => {
    if (selectedNumbers.length === numbersList.length) {
      setSelectedNumber([]);
    } else {
      setSelectedNumber(numbersList.map((number) => number.id));
    }
  };

  useEffect(() => {
    if (confirmDelete) {
      if (isMultiDelete) {
        postApiCall(API_NUMBER_DELETE, { numberIds: selectedNumbers }).then(onDeleteSuccess).catch(onDeleteFail);
      } else {
        postApiCall(API_NUMBER_DELETE, { numberIds: [deleteNumberId] }).then(onDeleteSuccess).catch(onDeleteFail);
      }
    }
  }, [confirmDelete]);

  function onDeleteSuccess(resp) {
    toast.success(resp.message || 'Number deleted successfully');
    let updatedList;
    if (isMultiDelete) {
      updatedList = numbersList
        .filter((val) => !selectedNumbers.includes(val.id))
        .map((val, key) => {
          val['key'] = key + 1;
          return val;
        });
      setSelectedNumber([]);
    } else {
      updatedList = numbersList
        .filter((val) => val.id !== deleteNumberId)
        .map((val, key) => {
          val['key'] = key + 1;
          return val;
        });
    }
    setNumbersList(updatedList);
    setEnableDeleteModal(false);
    setDeleteNumberId(undefined);
    setConfirmDelete(false);
    setLoading(true);
    getApiCall(API_NUMBERS_LIST).then(onNumberListSuccess).catch(onError).finally(() => setLoading(false));
  }

  function onDeleteFail(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      toast.error(friendlyApiError(err) || 'Unable to delete Number');
    }
    setConfirmDelete(false);
  }

  const openChangePlanModal = (rowData) => {
    setChangePlanNumber(rowData);
    setChangePlanSelected(rowData?.voice_plan_id?.id ?? null);
    setShowChangePlanModal(true);
  };

  const buildNumberPlanConstraint = (rowData) => {
    const constraints = {};
    const parentPlans = flattenPlansToArray(rowData?.voice_plan_id?.plans);
    for (const p of parentPlans) {
      const key = getVoicePlanKey(p);
      if (!key) continue;
      const price = Number(p.pulse_price);
      const duration = Number(p.pulse_duration);
      constraints[key] = {
        minPrice: Number.isFinite(price) ? price : 0,
        minDuration: Number.isFinite(duration) ? duration : 0,
      };
    }
    return constraints;
  };

  const isPlanValidForNumber = (plan, constraints) => {
    if (!constraints || Object.keys(constraints).length === 0) return true;
    const childPlans = flattenPlansToArray(plan?.plans);
    if (childPlans.length === 0) return false;
    for (const [key, rule] of Object.entries(constraints)) {
      const child = childPlans.find((x) => getVoicePlanKey(x) === key);
      if (!child) return false;
      if (Number(child.pulse_price) < rule.minPrice) return false;
      if (Number(child.pulse_duration) < rule.minDuration) return false;
    }
    return true;
  };

  const handleChangePlanSubmit = () => {
    if (isChangingPlan) return;
    if (!changePlanNumber?.id || !changePlanSelected) {
      toast.error('Please select a plan');
      return;
    }
    setIsChangingPlan(true);
    postApiCall(`${API_VOICE_PLAN_NUMBER_EDIT}/${changePlanNumber.id}`, {
      DID: changePlanNumber.id,
      voice_plan_id: changePlanSelected,
    })
      .then(() => {
        toast.success('Voice Plan updated successfully');
        setShowChangePlanModal(false);
        setChangePlanNumber(null);
        setChangePlanSelected(null);
        getApiCall(API_NUMBERS_LIST).then(onNumberListSuccess).catch(onError);
      })
      .catch((error) => {
        toast.error(friendlyApiError(error) || 'Failed to update voice plan');
      })
      .finally(() => setIsChangingPlan(false));
  };

  const RenderActionComponent = ({ id, number_type, routingType }) => {
    const numberEditRoute = ROUTES.NUMBERS.EDIT.replace(':id', id);
    const editDestinationRoute = ROUTES.IVR.UPDATE_INBOUND_CALLS.replace(':cid', id);
    return (
      <div className="flex align-items-center list-user-action">
        {canEditNumber && <EditIcon linkTo={`/${numberEditRoute}`} />}{' '}
        {!(selectedNumbers.length > 0) && canDeleteNumber && (
          <DeleteIcon handleCallback={() => handleDeleteNumber(id)} />
        )}{' '}
        {(routingType === 'IVR' ||
          routingType === 'AGENT' ||
          routingType === 'QUEUE' ||
          routingType === 'AI_AGENT') &&
          isCallcenterAdmin &&
          canEdit && (
            <button
              className="btn btn-sm btn-icon btn-primary"
              title="Edit Destination"
              onClick={() => navigate(`/${editDestinationRoute}`)}
              style={{ width: '36px', height: '36px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <FaEdit />
            </button>
          )}
      </div>
    );
  };

  const handleSetCallCentreDefault = (numberId, removeDefault = false) => {
    const payload = removeDefault ? { removeDefault: true } : { numberId };
    postApiCall(API_CALL_CENTRE_DEFAULT_CLI, payload)
      .then((resp) => {
        toast.success(resp?.message || (removeDefault ? 'Default CLI removed' : 'Default CLI updated'));
        setCallCentreDefaultDidId(removeDefault ? null : Number(numberId));
        setSelectedNumber([]);
      })
      .catch((err) => {
        toast.error(friendlyApiError(err) || 'Unable to update default CLI');
      });
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setCsvFile(file);
  };

  async function handleBulkAssign() {
    const formData = new FormData();
    formData.append('pool', 'pool');
    formData.append('phone_numbers', csvFile);
    formData.append('type', numberType);

    postApiMediaCall(API_BULK_ASSIGN_DID, formData)
      .then(() => {
        toast.success('Bulk DID assigned successfully');
        setShowBulkAssignModal(false);
        getApiCall(API_NUMBERS_LIST).then(onNumberListSuccess).catch(onError);
      })
      .catch((err) => {
        toast.error(friendlyApiError(err) || 'Failed to assign bulk DID');
      });
  }

  const handleAllocate = () => {
    const payload = {
      DID: selectedDID.label,
      DID_id: selectedDID.value,
      expiryDate,
      numberType: selectedNumberType,
      status: selectedStatus.toString(),
      id: allocateNumberId,
    };

    postApiCall(API_INDIVIDUAL_ASSIGN_DID, payload)
      .then(() => {
        toast.success('Number allocated successfully');
        setShowAllocateModal(false);
        setSelectedDID(null);
        setExpiryDate(undefined);
        getApiCall(API_NUMBERS_LIST).then(onNumberListSuccess).catch(onError);
      })
      .catch((err) => {
        toast.error(friendlyApiError(err) || 'Allocation failed');
      });
  };

  const handleBulkUpdate = () => {
    if (!updateNumberType) {
      toast.error('Number Type is required');
      return false;
    }
    if (!csvFile) {
      toast.error('Please choose a CSV file to upload');
      return false;
    }
    if (!/\.csv$/i.test(csvFile.name || '')) {
      toast.error('Only .csv files are supported for bulk update');
      return false;
    }
    const formData = new FormData();
    formData.append('phone_numbers', csvFile);
    formData.append('numberType', updateNumberType);

    setBulkUpdateInProgress(true);

    postApiMediaCall(API_NUMBER_EDIT_BULK, formData)
      .then((resp) => {
        const updated = Number(resp?.updatedCount ?? 0);
        const skipped = resp?.skippedRows || [];

        if (updated > 0) {
          toast.success(
            skipped.length > 0
              ? `Updated ${updated} number(s). ${skipped.length} row(s) skipped.`
              : `Successfully updated ${updated} number(s).`,
          );
        } else {
          toast.warning(`No numbers were updated. ${skipped.length} row(s) skipped.`);
        }

        setBulkUpdateSkippedRows(skipped);
        getApiCall(API_NUMBERS_LIST).then(onNumberListSuccess).catch(onError);
      })
      .catch((err) => {
        toast.error(friendlyApiError(err) || 'Bulk update failed');
      })
      .finally(() => setBulkUpdateInProgress(false));

    return true;
  };

  const renderDemoBadge = (rowData) => (
    <span className={`badge ${rowData.is_demo ? 'bg-success' : 'bg-danger'}`}>
      {rowData.is_demo ? 'Yes' : 'No'}
    </span>
  );

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const handleCreateButtonClick = () => {
    navigate(`/${ROUTES.NUMBERS.CREATE}`);
  };

  const resetAllocateModalState = () => {
    setSelectedDID(null);
    setSelectedStatus(null);
    setExpiryDate(null);
    setExpiryDateError('');
    setUpdateNumberType(null);
  };

  const handleExpiryDateChange = (e) => {
    const date = e.target.value;
    const today = new Date();
    const selectedDate = new Date(date);
    if (selectedDate <= today) {
      setExpiryDateError('Expiry date must be in the future');
    } else {
      setExpiryDateError('');
    }
    setExpiryDate(date);
  };

  const openNumberDetails = (rowData) => {
    setNumberDetailsRow(rowData);
    setShowNumberDetailsModal(true);
  };

  const renderRoutingTypeBody = (rowData, iconSize = 30) => {
    if (!rowData.routing_type) return '-';
    // Well diameter: 90% of iconSize so the glyph fits inside a tinted
    // rounded square. Icon glyph is a bit smaller than the well.
    const well = Math.max(24, Math.round(iconSize * 0.9));
    const glyph = Math.round(well * 0.55);
    const wellStyle: React.CSSProperties = {
      width: well,
      height: well,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 8,
    };
    if (rowData.routing_type === 'AGENT') {
      return (
        <span
          title="Agent"
          style={wellStyle}
          className="bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300"
        >
          <Headphones size={glyph} />
        </span>
      );
    }
    if (rowData.routing_type === 'IVR') {
      return (
        <span
          title="IVR"
          style={wellStyle}
          className="bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300"
        >
          <Voicemail size={glyph} />
        </span>
      );
    }
    if (rowData.routing_type === 'QUEUE' || rowData.routing_type === 'CALL GROUP') {
      return (
        <span
          title="Call Group"
          style={wellStyle}
          className="bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
        >
          <Users size={glyph} />
        </span>
      );
    }
    if (rowData.routing_type === 'AI_AGENT') {
      return (
        <span
          title="AI Agent"
          style={wellStyle}
          className="bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
        >
          <Bot size={glyph} />
        </span>
      );
    }
    return rowData.routing_type;
  };

  // `status` on a number record is meant to be a status_code (1 = Available
  // … 10 = Reject) — that's what's used everywhere else (bulk-upload
  // template, allocate dropdown) — but the edit-page dropdown used to save
  // `id` (10-18) instead, so some already-saved records hold an id, not a
  // status_code (fixed in NumberFormUpdate.tsx going forward). Try
  // status_code first since that's the correct/current convention; fall
  // back to `id` for those pre-existing records. If neither matches, the
  // value is already a resolved label — show it as-is.
  const resolveStatusLabel = (raw) => {
    if (raw === null || raw === undefined || raw === '') return '-';
    const key = String(raw);
    const match =
      allocateStatus.find((item) => String(item.status_code) === key) ??
      allocateStatus.find((item) => String(item.id) === key);
    return match ? match.status : raw;
  };

  const renderNumberStatusBody = (rowData) => {
    if (isSuperAdmin) {
      return !rowData.allocated_to || rowData.allocated_to.id === null ? resolveStatusLabel(rowData.status) : 'Allocated';
    }
    if (isReseller) {
      if (rowData.allocated_to && rowData.allocated_to.id === loggedUserId && rowData.allocated_to.type === loggedUserType) {
        return resolveStatusLabel(rowData.status);
      }
      return 'Allocated';
    }
    if (isCompanyUser) {
      if (rowData.allocated_to && rowData.allocated_to.id === loggedUser.company_id && rowData.allocated_to.type === loggedUserType) {
        return resolveStatusLabel(rowData.status);
      }
      return 'Allocated';
    }
    if (isCallcenterAdmin) {
      if (rowData.allocated_to && rowData.allocated_to.id === loggedUser.callcenter_id && rowData.allocated_to.type === loggedUserType) {
        return resolveStatusLabel(rowData.status);
      }
      return 'Allocated';
    }
    return resolveStatusLabel(rowData.status);
  };

  const dropdownOptionStatus = allocateStatus.map((item) => ({ label: item.status, value: item.status_code }));

  const getColumnsAndFilename = (nt) => {
    let columns;
    let filename;
    let sample_data;
    if (nt === 'DID') {
      columns = BULK_UPDATE_DID;
      filename = `${nt}-Update.csv`;
      sample_data = SAMPLE_DATA_DID_UPDATE;
    } else if (nt === 'VMN') {
      columns = BULK_UPDATE_OTHERS;
      filename = `${nt}-Update.csv`;
      sample_data = SAMPLE_DATA_OTHERS_UPDATE;
    } else {
      columns = BULK_UPDATE_OTHERS;
      filename = `${nt}-Update.csv`;
      sample_data = SAMPLE_DATA_OTHERS_UPDATE;
    }
    return { columns, filename, sample_data };
  };

  const getColumnsAndFilenameForAssignDID = (nt) => {
    let columns;
    let filename;
    if (nt === 'VMN') {
      columns = ASSIGN_DID_VMN;
      filename = `${nt}-AssignDID.csv`;
    } else {
      columns = ASSIGN_DID_TOLLFREE;
      filename = `${nt}-AssignDID.csv`;
    }
    return { columns, filename };
  };

  const showDemoTab = isSuperAdmin || isLoggedInDemoCompany;
  const numberTypeTabs = useMemo(
    () => getNumberTypeTabs(showDemoTab),
    [showDemoTab],
  );
  const selectedType = numberTypeTabs[activeIndex];

  useEffect(() => {
    // Wait for data (role is known by then) so a restored DEMO tab is not reset.
    if (originalNumbersList.length && activeIndex >= numberTypeTabs.length) {
      setActiveIndex(0);
      setNumberType(numberTypeTabs[0]);
    }
  }, [numberTypeTabs, activeIndex, originalNumbersList]);

  const typeFilteredList = useMemo(
    () => originalNumbersList.filter((item) => matchesNumberType(item, selectedType)),
    [originalNumbersList, selectedType],
  );

  const trunkOptions = useMemo(() => {
    const map = new Map();
    let hasNone = false;
    for (const row of typeFilteredList) {
      const id = getNumberTrunkId(row);
      if (!id) {
        hasNone = true;
        continue;
      }
      if (!map.has(id)) map.set(id, getNumberTrunkName(row) || `Trunk ${id}`);
    }
    return {
      options: [...map.entries()]
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
      hasNone,
    };
  }, [typeFilteredList]);

  const inventoryCounts = useMemo(() => {
    const counts = { routed: 0, allocated: 0, available: 0 };
    for (const row of typeFilteredList) {
      if (!matchesTrunk(row, selectedTrunkId)) continue;
      if (!matchesIntl(row, intlFilter)) continue;
      counts[getInventoryState(row)] += 1;
    }
    return counts;
  }, [typeFilteredList, selectedTrunkId, intlFilter]);

  useEffect(() => {
    if (selectedTrunkId === TRUNK_FILTER_ALL) return;
    // Keep a restored trunk filter until the list has loaded.
    if (!originalNumbersList.length) return;
    if (selectedTrunkId === TRUNK_FILTER_NONE) {
      if (!trunkOptions.hasNone) setSelectedTrunkId(TRUNK_FILTER_ALL);
      return;
    }
    if (!trunkOptions.options.some((trunk) => trunk.id === selectedTrunkId)) {
      setSelectedTrunkId(TRUNK_FILTER_ALL);
    }
  }, [trunkOptions, selectedTrunkId, originalNumbersList]);

  useEffect(() => {
    const filtered = originalNumbersList.filter((item) => {
      if (!matchesNumberType(item, selectedType)) return false;
      if (!matchesTrunk(item, selectedTrunkId)) return false;
      if (!matchesIntl(item, intlFilter)) return false;
      if (inventoryFilter && getInventoryState(item) !== inventoryFilter) return false;
      return true;
    });
    setNumbersList(filtered);
  }, [originalNumbersList, selectedType, selectedTrunkId, intlFilter, inventoryFilter]);

  const canRead = hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.NUMBERS, 'read');
  const isNumberWriter = NUMBER_WRITE_ROLES.includes(userRole);
  const canAdd =
    isNumberWriter && hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.NUMBERS, 'add');
  const canDelete =
    isNumberWriter && hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.NUMBERS, 'delete');
  const canEdit =
    isNumberWriter && hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.NUMBERS, 'edit');
  const canRemoveInboundRouting = canEdit;
  const canManageDemo = isSuperAdmin && canEdit;
  const canEditNumber = isSuperAdmin && canEdit;
  const canDeleteNumber = isSuperAdmin && canDelete;

  if (!canRead) {
    return (
      <div className="text-center p-4" style={{ marginTop: '50px', marginBottom: '50px', fontSize: '16px' }}>
        No read permission found in assigned ACL
      </div>
    );
  }

  if (loading && numbersList.length === 0) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  const viewNumberDetailsTemplate = (rowData) => (
    <RBButton
      variant="primary"
      className="numbers-list-view-btn w-100"
      style={{ height: '34px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '1.25' }}
      onClick={() => openNumberDetails(rowData)}
    >
      View
    </RBButton>
  );

  // Column definitions — v1 has TWO tables (DID/VMN/TOLL FREE/DEMO tab uses a full column set; the fallback
  // table has a slightly different column order). We render the primary tabbed table here — the fallback
  // path is unreachable in normal usage (activeIndex is always 0-3).
  const primaryColumns: ColumnDef<any>[] = [
    {
      id: '_select',
      enableSorting: false,
      enableHiding: false,
      header: () => (
        <Checkbox
          checked={selectedNumbers.length === numbersList.length && numbersList.length > 0}
          onCheckedChange={handleSelectAll}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedNumbers.includes(row.original.id)}
          onCheckedChange={() => handleCheckboxToggle(row.original.id)}
          aria-label="Select row"
        />
      ),
    },
    {
      accessorKey: 'actual_number',
      header: numberType || 'Number',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span className="tabular-nums">{formatListedNumber(row.original, countryCodes)}</span>
          {isSuperAdmin && (
            <button
              type="button"
              title="View channel consumption"
              aria-label="View channel consumption"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sky-600 hover:bg-sky-50 hover:text-sky-700 dark:text-sky-400 dark:hover:bg-sky-950/40"
              onClick={(e) => {
                e.stopPropagation();
                setConsumptionNumber(row.original);
                setShowConsumptionModal(true);
              }}
            >
              <Eye className="h-4 w-4" />
            </button>
          )}
          {Number(callCentreDefaultDidId) === Number(row.original.id) && (
            <Badge variant="success">Default</Badge>
          )}
        </div>
      ),
    },
    ...(!(isReseller || isCompanyUser)
      ? [
          {
            accessorKey: 'routing_type',
            header: 'Routing Type',
            cell: ({ row }) => renderRoutingTypeBody(row.original),
          } as ColumnDef<any>,
          {
            id: 'routing_destination',
            accessorFn: (row) => formatRoutingDestination(row),
            header: 'Routing Destination',
            cell: ({ row }) => formatRoutingDestination(row.original),
          } as ColumnDef<any>,
        ]
      : []),
    { accessorKey: 'number_type', header: 'Number Type', cell: ({ row }) => row.original.number_type || '-' },
    {
      id: 'trunk',
      accessorFn: (row) => getNumberTrunkName(row),
      header: 'Trunk',
      cell: ({ row }) => getNumberTrunkName(row.original) || '-',
    },
    {
      id: 'is_international',
      accessorFn: (row) => (Number(row.is_international) === 1 ? 'International' : 'Domestic'),
      header: 'International / Domestic',
      cell: ({ row }) =>
        Number(row.original.is_international) === 1 ? (
          <span className="inline-flex items-center whitespace-nowrap rounded-full bg-sky-100 px-2.5 py-0.5 text-[11px] font-semibold text-sky-700 ring-1 ring-inset ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300">
            International
          </span>
        ) : (
          <span className="inline-flex items-center whitespace-nowrap rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">
            Domestic
          </span>
        ),
    },
    { id: 'status', accessorKey: 'status', header: 'Status', cell: ({ row }) => renderNumberStatusBody(row.original) },
    { accessorKey: 'is_demo', header: 'Demo', cell: ({ row }) => renderDemoBadge(row.original) },
    {
      id: 'plan_name',
      accessorFn: (row) => row.voice_plan_id?.plan_name ?? '',
      header: 'Plan Name',
      cell: ({ row }) => {
        const rowData = row.original;
        const planName = rowData?.voice_plan_id?.plan_name;
        return (
          <div className="flex align-items-center" style={{ gap: 6 }}>
            {planName ? (
              <button
                className="plan-name-chip"
                onClick={() => {
                  setSelectedPlanDetails(rowData.voice_plan_id.plans || []);
                  setSelectedPlanName(planName);
                  setShowPlanModal(true);
                }}
              >
                {planName}
              </button>
            ) : (
              <span>-</span>
            )}
          </div>
        );
      },
    },
    { accessorKey: 'number_routing_type', header: 'Number routing', cell: ({ row }) => numberRoutingTypeCell(row.original) },
    { accessorFn: (row) => row.allocated_to?.name, id: 'allocated_to_name', header: 'Allocated To' },
    { accessorFn: (row) => row.allocated_to?.type, id: 'allocated_to_type', header: 'Allocated Type' },
    {
      id: 'createdAt',
      accessorFn: (row) => formatNumberCreatedAt(row) ?? '',
      header: 'Created Date',
      cell: ({ row }) => formatNumberCreatedAt(row.original) || '-',
    },
    { id: 'details', header: 'Details', cell: ({ row }) => viewNumberDetailsTemplate(row.original) },
    ...(canEditNumber || canDeleteNumber || (isCallcenterAdmin && canEdit)
      ? [
          {
            id: 'actions',
            header: 'Action',
            enableSorting: false,
            cell: ({ row }) => (
              <RenderActionComponent
                id={row.original.id}
                number_type={row.original.number_type}
                routingType={row.original.routing_type}
              />
            ),
          } as ColumnDef<any>,
        ]
      : []),
  ];

  return (
    <FillPage>
      <PageHeader
        title="Numbers"
        description="DID / VMN / Toll-free numbers and their inbound routing assignments."
        actions={
          <>
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                setActiveIndex(0);
                setNumberType('DID');
                setShowAssignDidButton(false);
                setSelectedTrunkId(TRUNK_FILTER_ALL);
                setInventoryFilter('');
                getApiCall(API_NUMBERS_LIST).then(onNumberListSuccess).catch(onError);
              }}
              title="Refresh"
              aria-label="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            {canAdd && (
              <Button onClick={handleCreateButtonClick}>
                <Plus className="h-4 w-4" />
                Create Number
              </Button>
            )}
            {userRole === USER_ROLES.CALL_CENTRE_ADMIN && (
              <Button
                onClick={() => navigate(`/${ROUTES.IVR.INBOUND_CALLS.replace(':cid', contentId)}`)}
              >
                <PhoneIncoming className="h-4 w-4" />
                Inbound Routing
              </Button>
            )}
          </>
        }
      />
      <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {[
          { key: 'routed', label: 'Routed', count: inventoryCounts.routed, Icon: PhoneForwarded, tone: 'sky' },
          { key: 'allocated', label: 'Allocated', count: inventoryCounts.allocated, Icon: UserCheck, tone: 'amber' },
          { key: 'available', label: 'Available', count: inventoryCounts.available, Icon: CircleCheck, tone: 'emerald' },
        ].map(({ key, label, count, Icon, tone }) => {
          const selected = inventoryFilter === key;
          const toneSelected =
            tone === 'sky'
              ? 'border-sky-500 bg-sky-50 shadow-[0_0_0_3px_rgba(14,165,233,0.28)] dark:border-sky-400 dark:bg-sky-950/50'
              : tone === 'amber'
                ? 'border-amber-500 bg-amber-50 shadow-[0_0_0_3px_rgba(245,158,11,0.28)] dark:border-amber-400 dark:bg-amber-950/50'
                : 'border-emerald-500 bg-emerald-50 shadow-[0_0_0_3px_rgba(16,185,129,0.28)] dark:border-emerald-400 dark:bg-emerald-950/50';
          const toneIdle =
            tone === 'sky'
              ? 'border-sky-200 hover:border-sky-300 dark:border-sky-900'
              : tone === 'amber'
                ? 'border-amber-200 hover:border-amber-300 dark:border-amber-900'
                : 'border-emerald-200 hover:border-emerald-300 dark:border-emerald-900';
          const labelTone =
            tone === 'sky'
              ? 'text-sky-700 dark:text-sky-300'
              : tone === 'amber'
                ? 'text-amber-700 dark:text-amber-300'
                : 'text-emerald-700 dark:text-emerald-300';
          return (
            <button
              key={key}
              type="button"
              onClick={() => setInventoryFilter((prev) => (prev === key ? '' : key))}
              aria-pressed={selected}
              title={selected ? `${label} filter on — click again to show all` : `Show ${label.toLowerCase()} numbers`}
              className="text-left"
            >
              <Card
                className={
                  'flex items-center justify-between border-2 p-4 transition-all ' +
                  (selected ? toneSelected : `${toneIdle} hover:shadow-md`)
                }
              >
                <div>
                  <div className={'text-xs font-semibold ' + (selected ? labelTone : 'text-muted-foreground')}>
                    {label}
                    {selected && (
                      <span className={'ml-2 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ' + (
                        tone === 'sky'
                          ? 'bg-sky-500 text-white'
                          : tone === 'amber'
                            ? 'bg-amber-500 text-white'
                            : 'bg-emerald-500 text-white'
                      )}>
                        <Check className="h-2.5 w-2.5" />
                        Selected
                      </span>
                    )}
                  </div>
                  <div className={'mt-1 text-2xl font-semibold tabular-nums ' + (selected ? labelTone : 'text-foreground')}>
                    {count}
                  </div>
                </div>
                <span
                  className={
                    'flex h-10 w-10 items-center justify-center rounded-md ' +
                    (tone === 'sky'
                      ? 'bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300'
                      : tone === 'amber'
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
                        : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300')
                  }
                >
                  <Icon className="h-5 w-5" />
                </span>
              </Card>
            </button>
          );
        })}
      </div>

      {[USER_ROLES.CALL_CENTRE_ADMIN, USER_ROLES.SUPERADMIN, USER_ROLES.SUB_SUPERADMIN].includes(userRole) && (
        <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="flex h-5 w-5 items-center justify-center rounded-md bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300">
              <Headphones className="h-3 w-3" />
            </span>
            Agent
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="flex h-5 w-5 items-center justify-center rounded-md bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
              <Voicemail className="h-3 w-3" />
            </span>
            IVR
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="flex h-5 w-5 items-center justify-center rounded-md bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
              <Users className="h-3 w-3" />
            </span>
            Call Group
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="flex h-5 w-5 items-center justify-center rounded-md bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
              <Bot className="h-3 w-3" />
            </span>
            AI Agent
          </span>
        </div>
      )}

      {/* Type tabs (left) + trunk filter + secondary actions (right) — same line. */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
        <div
          className="inline-flex h-10 items-center gap-1 rounded-full border border-border/60 bg-card p-1"
          style={{
            boxShadow:
              'inset 2px 2px 5px rgba(163,177,198,0.35), inset -2px -2px 5px rgba(255,255,255,0.9)',
          }}
        >
          {numberTypeTabs.map((tab, index) => (
            <button
              key={tab}
              type="button"
              onClick={() => {
                setActiveIndex(index);
                setNumberType(tab);
                setShowAssignDidButton(['VMN', 'TOLL FREE'].includes(tab));
                setBulkUpdateButton(true);
              }}
              className={
                'rounded-full px-4 py-1.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 ' +
                (activeIndex === index
                  ? 'bg-primary text-primary-foreground shadow-[0_2px_5px_rgba(0,0,0,0.15)]'
                  : 'text-muted-foreground hover:bg-primary/10 hover:text-primary')
              }
            >
              {tab}
            </button>
          ))}
        </div>
        <Select value={selectedTrunkId} onValueChange={setSelectedTrunkId}>
          <SelectTrigger className="h-10 w-[200px]" aria-label="Filter by trunk">
            <SelectValue placeholder="All trunks" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TRUNK_FILTER_ALL}>All trunks</SelectItem>
            {trunkOptions.hasNone && (
              <SelectItem value={TRUNK_FILTER_NONE}>No trunk</SelectItem>
            )}
            {trunkOptions.options.map((trunk) => (
              <SelectItem key={trunk.id} value={trunk.id}>
                {trunk.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={intlFilter} onValueChange={setIntlFilter}>
          <SelectTrigger className="h-10 w-[200px]" aria-label="Filter by International / Domestic">
            <SelectValue placeholder="International / Domestic" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">International + Domestic</SelectItem>
            <SelectItem value="international">International</SelectItem>
            <SelectItem value="domestic">Domestic</SelectItem>
          </SelectContent>
        </Select>
        </div>

        <div className="flex flex-wrap items-center gap-2">
        {canRemoveInboundRouting && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleRemoveInboundRouting}
            disabled={selectedNumbers.length === 0}
          >
            Remove Inbound Routing
          </Button>
        )}
        {canManageDemo && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleSetDemo(true)}
            disabled={selectedNumbers.length === 0 || setDemoLoading}
          >
            Move to Demo
          </Button>
        )}
        {canManageDemo && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleSetDemo(false)}
            disabled={selectedNumbers.length === 0 || setDemoLoading}
          >
            Remove from Demo
          </Button>
        )}
        {isCallcenterAdmin && canEdit && (
          <Button
            size="sm"
            onClick={() =>
              handleSetCallCentreDefault(
                selectedNumbers[0],
                selectedNumbers.length === 1 &&
                  Number(callCentreDefaultDidId) === Number(selectedNumbers[0]),
              )
            }
            disabled={selectedNumbers.length !== 1}
          >
            {selectedNumbers.length === 1 &&
            Number(callCentreDefaultDidId) === Number(selectedNumbers[0])
              ? 'Remove Default'
              : 'Mark As Default'}
          </Button>
        )}
        {numbersList.length > 0 && (
          <Button size="sm" variant="outline" onClick={handleExport} disabled={buttonDisable}>
            Export Data
            {buttonDisable && (
              <span
                className="spinner-border spinner-border-sm ml-2"
                role="status"
                aria-hidden="true"
              />
            )}
          </Button>
        )}
        {bulkUpdateButton && canManageDemo && (
          <Button size="sm" variant="outline" onClick={() => setBulkUpdateModal(true)}>
            Bulk Update
          </Button>
        )}
        </div>
      </div>

      <div className="scroll-container numbers-list-datatable-wrap">
        <DataTable
          tableId={`numbers.list.${numberTypeTabs[activeIndex] || 'DID'}`}
          columns={primaryColumns}
          data={numbersList}
          pageSize={10}
          disableExport
          toolbar={
            isSuperAdmin && (
              <Button
                size="sm"
                variant="destructive"
                onClick={handleMultiDelete}
                disabled={selectedNumbers.length === 0}
              >
                Delete Selected
              </Button>
            )
          }
        />
      </div>

      <DidChannelConsumptionModal
        open={showConsumptionModal}
        onClose={() => {
          setShowConsumptionModal(false);
          setConsumptionNumber(null);
        }}
        numberRow={consumptionNumber}
        displayNumber={formatListedNumber(consumptionNumber, countryCodes)}
      />

      {/* Number Details Modal */}
      <Modal
        show={showNumberDetailsModal}
        onHide={() => { setShowNumberDetailsModal(false); setNumberDetailsRow(null); }}
        size="lg"
        centered
        className="gap-0 overflow-hidden p-0"
        style={{ maxHeight: '94vh' }}
      >
        <Modal.Header closeButton className="space-y-0 border-b border-border bg-primary/5 px-6 py-4">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
              <FiPhone size={16} aria-hidden />
            </span>
            <Modal.Title className="text-base font-semibold">Number Details</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body className="px-6 py-3">
          <div className="grid gap-6 md:grid-cols-5">
            <div className="md:col-span-3">
              <NumberDetailItem icon={FiPhone} iconTone="blue" label="Number">
                <strong className="tabular-nums">{formatListedNumber(numberDetailsRow, countryCodes)}</strong>
              </NumberDetailItem>
              <NumberDetailItem icon={FiGlobe} iconTone="green" label="Country Code">
                <CountryCodeValue code={numberDetailsRow?.country_code} countries={countryCodes} />
              </NumberDetailItem>
              <NumberDetailItem icon={FiMapPin} iconTone="purple" label="State Code" value={numberDetailsRow?.state_code} />
              {!(isReseller || isCompanyUser) && (
                <NumberDetailItem icon={FiDollarSign} iconTone="amber" label="Cost" value={numberDetailsRow?.cost ?? '-'} />
              )}
              {!(isReseller || isCompanyUser) && (
                <NumberDetailItem icon={FiBriefcase} iconTone="blue" label="Operator" value={numberDetailsRow?.operator} />
              )}
              {!(isReseller || isCompanyUser) && (
                <NumberDetailItem icon={FiTag} iconTone="rose" label="Category">
                  {numberDetailsRow?.category ? (
                    <NumberDetailBadge tone={String(numberDetailsRow.category).toLowerCase() === 'free' ? 'green' : 'amber'}>
                      {numberDetailsRow.category}
                    </NumberDetailBadge>
                  ) : (
                    '-'
                  )}
                </NumberDetailItem>
              )}
              {!(isReseller || isCompanyUser) && (
                <NumberDetailItem icon={FiDollarSign} iconTone="green" label="Currency" value={numberDetailsRow?.currency} />
              )}
              <NumberDetailItem icon={FiShare2} iconTone="purple" label="Trunk">
                {numberDetailsRow?.trunkId?.name ? (
                  <NumberDetailBadge tone="blue">{numberDetailsRow.trunkId.name}</NumberDetailBadge>
                ) : (
                  '-'
                )}
              </NumberDetailItem>
            </div>
            <div className="flex flex-col items-center justify-center gap-3 rounded-lg bg-muted/40 px-4 py-6 text-center md:col-span-2 md:border-l md:border-border">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
                <FiPhone size={28} aria-hidden />
              </span>
              <div>
                <h5 className="text-sm font-semibold text-foreground">Number Overview</h5>
                <p className="mt-1 text-xs text-muted-foreground">
                  View detailed information about this number.
                </p>
              </div>
              <div className="mt-2 flex items-center gap-1.5 border-t border-border pt-3 text-xs text-muted-foreground">
                <FiClock size={13} aria-hidden />
                <span>
                  {formatNumberCreatedAt(numberDetailsRow)
                    ? `Added on ${formatNumberCreatedAt(numberDetailsRow)}`
                    : 'Created date unavailable'}
                </span>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Remove Inbound Routing Confirm Modal */}
      <Modal
        show={showRemoveRoutingModal}
        onHide={() => !removeRoutingLoading && setShowRemoveRoutingModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Remove Inbound Routing</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to remove inbound routing for the selected {selectedNumbers.length} number(s)?
        </Modal.Body>
        <Modal.Footer>
          <RBButton variant="secondary" onClick={() => setShowRemoveRoutingModal(false)} disabled={removeRoutingLoading}>
            Cancel
          </RBButton>
          <RBButton variant="danger" onClick={confirmRemoveInboundRouting} disabled={removeRoutingLoading}>
            {removeRoutingLoading ? 'Removing...' : 'Remove'}
          </RBButton>
        </Modal.Footer>
      </Modal>

      <RenderDeleteModal
        show={enableDeleteModal}
        setShow={setEnableDeleteModal}
        setConfirmDelete={setConfirmDelete}
        isMultiDelete={isMultiDelete}
        clearSelectedData={() => setSelectedNumber([])}
      />

      {/* Individual Allocate DID Modal */}
      {showAllocateModal && (
        <Modal show={showAllocateModal} onHide={() => { setShowAllocateModal(false); resetAllocateModalState(); }}>
          <Modal.Header closeButton>
            <Modal.Title>Allocate DID</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group controlId="formDid">
                <Form.Label>Select DID</Form.Label>
                <Form.Select
                  value={selectedDID?.value || ''}
                  onChange={(e) => {
                    const opt = didOptions.find((o) => o.value === e.target.value);
                    setSelectedDID(opt);
                  }}
                >
                  <option value="">Select a DID</option>
                  {didOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group controlId="formStatus">
                <Form.Label>Select Status</Form.Label>
                <Form.Select value={selectedStatus || ''} onChange={(e) => setSelectedStatus(e.target.value)}>
                  <option value="">Select Status</option>
                  {dropdownOptionStatus.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {selectedStatus &&
                ['Reserved', 'Blocked'].includes(dropdownOptionStatus.find((option) => option.value === selectedStatus)?.label) && (
                  <div className="expiry-date-container">
                    <Form.Group as={Col} controlId="expiryDate">
                      <Form.Label>Expiry Date</Form.Label>
                      <CalendarDateInput
                        value={expiryDate || ''}
                        onChange={(v) => handleExpiryDateChange({ target: { value: v } } as any)}
                      />
                      <Form.Control.Feedback type="invalid">{expiryDateError}</Form.Control.Feedback>
                    </Form.Group>
                  </div>
                )}
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <RBButton variant="secondary" onClick={() => { setShowAllocateModal(false); resetAllocateModalState(); }}>
              Close
            </RBButton>
            <RBButton variant="primary" onClick={handleAllocate}>
              Submit
            </RBButton>
          </Modal.Footer>
        </Modal>
      )}

      {/* Bulk Assign DID Modal */}
      <Modal show={showBulkAssignModal} onHide={() => setShowBulkAssignModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '16px' }}>Bulk Assign DID for {numberType}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <RBButton
              variant="primary"
              onClick={(event) => {
                const { columns, filename } = getColumnsAndFilenameForAssignDID(numberType);
                downloadTemplate(event, columns, filename);
              }}
              style={{ marginBottom: '15px' }}
            >
              Download Template
            </RBButton>
            <Form.Group controlId="formFile">
              <Form.Control type="file" onChange={(e) => handleFileUpload(e)} accept=".csv" />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <RBButton variant="secondary" onClick={() => { setShowBulkAssignModal(false); setShowAssignDidButton(false); }}>
            Close
          </RBButton>
          <RBButton variant="primary" onClick={handleBulkAssign}>
            Upload
          </RBButton>
        </Modal.Footer>
      </Modal>

      {/* Bulk Update Modal */}
      <Modal show={bulkUpdateModal} onHide={() => setBulkUpdateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Bulk Update</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="row">
              <Form.Group className="col-md-12 form-group">
                <Form.Label>Select Number Type</Form.Label>
                <Form.Select
                  id="numberType"
                  value={updateNumberType}
                  className="p-2"
                  onChange={(e) => setUpdateNumberType(e.target.value)}
                  required
                >
                  <option value={''}>Select Number Type</option>
                  {NUMBERS_TYPES.map(function (val, idx) {
                    return (
                      <option key={idx} value={val.value}>
                        {val.label}
                      </option>
                    );
                  })}
                </Form.Select>
              </Form.Group>
            </div>
            <div>
              {updateNumberType && (
                <>
                  <RBButton
                    variant="primary"
                    onClick={(event) => {
                      const { columns, filename, sample_data } = getColumnsAndFilename(updateNumberType);
                      downloadTemplate(event, columns, filename, null, sample_data);
                    }}
                    style={{
                      marginBottom: '15px',
                      backgroundColor: '#3a57e8',
                      borderColor: '#3a57e8',
                      fontSize: '16px',
                      borderRadius: '5px',
                      boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    Download Template
                  </RBButton>
                  <div
                    style={{
                      background: '#FFF8C6',
                      color: '#6B4E16',
                      padding: '14px 16px',
                      borderRadius: '8px',
                      border: '1px solid #FFE08A',
                      fontSize: '14px',
                      lineHeight: '1.6',
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: '6px' }}>⚠️ Important Information</div>
                    <ul style={{ margin: 0, paddingLeft: '18px' }}>
                      <li>
                        Set <b>Number Routing</b> in the template using <b>incoming</b>, <b>outgoing</b>, or <b>both</b>.
                      </li>
                      <li>
                        Please use the following values to set the respective status in the Status column of the downloaded template:
                        <ul>
                          <li><strong>1</strong> – Available</li>
                          <li><strong>2</strong> – Reserved</li>
                          <li><strong>3</strong> – Blocked</li>
                          <li><strong>4</strong> – Archived</li>
                          <li><strong>6</strong> – Demo</li>
                          <li><strong>7</strong> – Live</li>
                          <li><strong>8</strong> – Delete</li>
                          <li><strong>9</strong> – Pending</li>
                          <li><strong>10</strong> – Reject</li>
                        </ul>
                      </li>
                      <li>
                        <b>Calling Code</b> is set automatically from each row's <b>Country Code</b> (e.g. IN → +91) and
                        can't be entered in the template.
                      </li>
                    </ul>
                  </div>
                  <hr />
                </>
              )}
            </div>
            {updateNumberType && (
              <Form.Group controlId="formFile">
                <Form.Label>Upload </Form.Label>
                <Form.Control type="file" onChange={(e) => handleFileUpload(e)} accept=".csv,text/csv" />
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <RBButton variant="secondary" onClick={() => { setBulkUpdateModal(false); resetAllocateModalState(); setShowAssignDidButton(false); }}>
            Close
          </RBButton>
          <RBButton
            variant="primary"
            disabled={bulkUpdateInProgress}
            onClick={() => {
              if (!handleBulkUpdate()) return;
              setBulkUpdateModal(false);
              resetAllocateModalState();
              setShowAssignDidButton(false);
            }}
          >
            {bulkUpdateInProgress ? 'Uploading...' : 'Upload'}
          </RBButton>
        </Modal.Footer>
      </Modal>

      {/* Skipped Rows Modal */}
      <Modal show={bulkUpdateSkippedRows.length > 0} onHide={() => setBulkUpdateSkippedRows([])} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '16px' }}>Skipped Rows ({bulkUpdateSkippedRows.length})</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: '13px', color: '#6B4E16', background: '#FFF8C6', border: '1px solid #FFE08A', borderRadius: '8px', padding: '10px 12px' }}>
            These rows were not applied. Every other row in the file was updated.
          </p>
          <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
            <table className="table table-sm table-bordered mb-0">
              <thead>
                <tr>
                  <th style={{ width: '70px' }}>Row</th>
                  <th style={{ width: '130px' }}>DID</th>
                  <th style={{ width: '130px' }}>Column</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {bulkUpdateSkippedRows.map((r, idx) => (
                  <tr key={`${r.row}-${r.did}-${idx}`}>
                    <td>{r.row ?? '-'}</td>
                    <td>{r.did ?? '-'}</td>
                    <td>{r.column ?? '-'}</td>
                    <td>{r.reason ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <RBButton
            variant="secondary"
            onClick={() =>
              exportToCSV({
                fileName: 'numbers-bulk-update-skipped-rows',
                data: bulkUpdateSkippedRows,
                columns: [
                  { header: 'Row', accessor: (r) => r.row ?? '-' },
                  { header: 'DID', accessor: (r) => r.did ?? '-' },
                  { header: 'Column', accessor: (r) => r.column ?? '-' },
                  { header: 'Reason', accessor: (r) => r.reason ?? '-' },
                ],
              })
            }
          >
            Export Skipped Rows
          </RBButton>
          <RBButton variant="primary" onClick={() => setBulkUpdateSkippedRows([])}>
            Close
          </RBButton>
        </Modal.Footer>
      </Modal>

      {/* Plan Details Modal */}
      <Modal show={showPlanModal} onHide={() => setShowPlanModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Plan Details - {selectedPlanName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(() => {
            let plans: any = selectedPlanDetails;
            if (typeof plans === 'string') {
              try { plans = JSON.parse(plans); } catch { plans = null; }
            }
            const entries =
              plans && typeof plans === 'object' && !Array.isArray(plans)
                ? Object.entries(plans)
                : Array.isArray(plans)
                  ? [['', plans]]
                  : [];

            const hasData = entries.some(([, v]) => (Array.isArray(v) ? v.length > 0 : v && typeof v === 'object'));
            if (!hasData) return <div>No plan details available.</div>;

            return entries.map(([category, value]) => {
              const rows = Array.isArray(value) ? value : [value];
              return (
                <div key={category} style={{ marginBottom: '16px' }}>
                  {category && <h6 style={{ fontWeight: 700, marginBottom: '8px' }}>{category}</h6>}
                  {rows.map((plan, index) => (
                    <div key={index} style={{ marginBottom: '8px', padding: '8px 12px', borderRadius: '8px', background: 'rgba(0,0,0,0.04)' }}>
                      {plan.leg_type && (
                        <>
                          <strong>{plan.leg_type}</strong>
                          <br />
                        </>
                      )}
                      {/* Price (derived per-second rate, pulse_price/100/pulse_duration)
                          hidden — billing never uses it and its 4-dp rounding
                          misreads as the real rate. Pulse Price/Duration below
                          are what actually bill. */}
                      {/* Price: {plan.price}
                      <br /> */}
                      Pulse Duration: {plan.pulse_duration}
                      <br />
                      Pulse Price: {plan.pulse_price}
                    </div>
                  ))}
                </div>
              );
            });
          })()}
        </Modal.Body>
      </Modal>

      {/* Change Voice Plan Modal */}
      <Modal show={showChangePlanModal} onHide={() => setShowChangePlanModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Change Voice Plan{changePlanNumber?.actual_number ? ` - ${changePlanNumber.actual_number}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(() => {
            const constraint = changePlanNumber ? buildNumberPlanConstraint(changePlanNumber) : {};
            const options = (Array.isArray(voicePlanList) ? voicePlanList : []).map((plan) => ({
              label: plan.plan_name,
              value: plan.id,
              disabled: !isPlanValidForNumber(plan, constraint),
            }));
            return (
              <>
                <div style={{ marginBottom: 8 }}>
                  <strong>Current Plan:</strong> {changePlanNumber?.voice_plan_id?.plan_name || 'None'}
                </div>
                <label style={{ fontWeight: 600, marginBottom: 6, display: 'block' }}>Select New Plan</label>
                <Form.Select
                  value={changePlanSelected || ''}
                  onChange={(e) => setChangePlanSelected(Number(e.target.value))}
                >
                  <option value="">Select a voice plan</option>
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
                <small style={{ display: 'block', marginTop: 8, color: '#6c757d' }}>
                  Plans below the current plan's pulse price/duration are disabled to keep the plan hierarchy valid.
                </small>
              </>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <RBButton variant="secondary" onClick={() => setShowChangePlanModal(false)} disabled={isChangingPlan}>
            Cancel
          </RBButton>
          <RBButton
            variant="primary"
            onClick={handleChangePlanSubmit}
            disabled={isChangingPlan || !changePlanSelected || changePlanSelected === changePlanNumber?.voice_plan_id?.id}
          >
            {isChangingPlan ? 'Updating...' : 'Update Plan'}
          </RBButton>
        </Modal.Footer>
      </Modal>
    </FillPage>
  );
}
